// ============================================
// IMPORTADORCSV.JS — Wizard 3 pasos para importar sucursales masivamente
// ============================================

import React, { useMemo, useRef, useState } from 'react';
import Papa from 'papaparse';
import {
  API_BASE_CLI, PP, COLOR_PRINCIPAL, COLOR_OSCURO, COLOR_FONDO, COLOR_GRIS,
  COLOR_GRIS_CLARO, COLOR_BORDE,
  estiloInput, estiloBotonPrimario, estiloBotonSecundario, estiloTarjeta,
} from './helpersClientes';
import { authHeaders } from '../utils/storage';

const COLUMNAS = [
  'grupo', 'razon_social', 'sucursal_nombre', 'telefono_sucursal',
  'direccion_link', 'direccion_manual', 'notas_acceso',
  'encargado_nombre', 'encargado_celular', 'encargado_puesto',
  'gerente_nombre', 'gerente_celular', 'gerente_puesto',
];

function normalizar(s) {
  if (!s) return '';
  return String(s).toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ').trim();
}

// Datos de ejemplo que van en la plantilla (reutilizados para CSV y TSV)
const EJEMPLOS = [
  {
    grupo: 'Bodegas Alianza',
    razon_social: 'Bodegas Alianza 1 S.A. de C.V.',
    sucursal_nombre: 'Tlalpan',
    telefono_sucursal: '55-1234-5678',
    direccion_link: 'https://maps.google.com/?q=19.2835,-99.1647',
    direccion_manual: '',
    notas_acceso: 'Timbre azul, 3er piso',
    encargado_nombre: 'Juan Pérez',
    encargado_celular: '55-1111-2222',
    encargado_puesto: 'Encargado de turno',
    gerente_nombre: 'Luis Ángel Ramírez',
    gerente_celular: '55-3333-4444',
    gerente_puesto: 'Gerente regional',
  },
  {
    grupo: 'Bodegas Alianza',
    razon_social: 'Bodegas Alianza 1 S.A. de C.V.',
    sucursal_nombre: 'Cuernavaca',
    telefono_sucursal: '777-5555-6666',
    direccion_link: '',
    direccion_manual: 'Av. Reforma 100, Col. Centro',
    notas_acceso: '',
    encargado_nombre: 'María López',
    encargado_celular: '',
    encargado_puesto: '',
    gerente_nombre: 'Luis Ángel Ramírez',
    gerente_celular: '55-3333-4444',
    gerente_puesto: 'Gerente regional',
  },
];

export default function ImportadorCSV({ onCerrar, onCompletado }) {
  const [paso, setPaso] = useState(1); // 1=subir, 2=preview, 3=importando/completado
  const [archivo, setArchivo] = useState(null);
  const [filasRaw, setFilasRaw] = useState([]);
  const [validacion, setValidacion] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [estado, setEstado] = useState(null);
  const [errorCarga, setErrorCarga] = useState(null);
  const [modalPlantilla, setModalPlantilla] = useState(false);
  const [toastPlantilla, setToastPlantilla] = useState(null);
  const pollRef = useRef(null);

  // ============================================
  // PASO 1: lectura del archivo
  // ============================================
  const leerArchivo = (file) => {
    setErrorCarga(null);
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) {
      setErrorCarga('El archivo debe tener extensión .csv');
      return;
    }
    setArchivo(file);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => h.trim().toLowerCase(),
      complete: (res) => {
        setFilasRaw(res.data || []);
        const val = validar(res.data || []);
        setValidacion(val);
        setPaso(2);
      },
      error: (err) => setErrorCarga('Error leyendo CSV: ' + err.message),
    });
  };

  // Descarga CSV con BOM UTF-8 para que Excel interprete correctamente
  // los acentos y la ñ (por defecto Excel abre CSVs como Latin-1).
  const descargarPlantillaExcel = () => {
    const csv = Papa.unparse({ fields: COLUMNAS, data: EJEMPLOS });
    const BOM = '﻿';
    const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'plantilla_sucursales.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setModalPlantilla(false);
  };

  // Copia datos en TSV al portapapeles y abre Google Sheets nuevo.
  // Sheets detecta los tabuladores al pegar y los distribuye en columnas.
  const abrirEnGoogleSheets = async () => {
    const tsv = [
      COLUMNAS.join('\t'),
      ...EJEMPLOS.map(fila => COLUMNAS.map(c => String(fila[c] || '').replace(/\t/g, ' ').replace(/\n/g, ' ')).join('\t')),
    ].join('\n');
    try {
      await navigator.clipboard.writeText(tsv);
      window.open('https://docs.google.com/spreadsheets/u/0/create', '_blank');
      setToastPlantilla('✅ Datos copiados al portapapeles. Pega con Ctrl+V en el Sheet que se abrió.');
      setTimeout(() => setToastPlantilla(null), 8000);
    } catch {
      setToastPlantilla('⚠️ No pude copiar al portapapeles. Usa la opción de Excel.');
      setTimeout(() => setToastPlantilla(null), 6000);
    }
  };

  // ============================================
  // PASO 2: validación client-side
  // ============================================
  const iniciarImportacion = async () => {
    setPaso(3);
    const filasOk = (validacion.filas || []).filter(f => f.estado !== 'error');
    try {
      const r = await fetch(`${API_BASE_CLI}/api/clientes/importar-masivo`, {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ filas: filasOk.map(f => f.data) }),
      }).then(x => x.json());
      if (!r.ok) throw new Error(r.error);
      setJobId(r.jobId);
      iniciarPolling(r.jobId);
    } catch (e) {
      setEstado({ estado: 'error', errorFatal: e.message });
    }
  };

  const iniciarPolling = (id) => {
    const tick = async () => {
      try {
        const r = await fetch(`${API_BASE_CLI}/api/clientes/importar-masivo/status/${id}`, { headers: authHeaders() }).then(x => x.json());
        if (r.ok) {
          setEstado(r);
          if (r.estado === 'completado' || r.estado === 'error') {
            if (pollRef.current) clearInterval(pollRef.current);
          }
        }
      } catch {
        // si falla el polling, reintentar en el siguiente tick
      }
    };
    tick();
    pollRef.current = setInterval(tick, 2000);
  };

  // Limpieza al desmontar
  React.useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // ============================================
  // UI
  // ============================================
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(26,58,92,0.55)', zIndex: 1200,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={() => paso === 3 && estado?.estado === 'completado' ? null : onCerrar()}>
      <div
        style={{
          background: '#fff', borderRadius: 14, padding: 0, width: '100%', maxWidth: 720,
          fontFamily: PP, color: COLOR_GRIS, maxHeight: '92vh', overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          padding: '20px 24px', borderBottom: `1px solid ${COLOR_BORDE}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: COLOR_OSCURO }}>
              📥 Importar sucursales desde CSV
            </div>
            <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 4 }}>
              Paso {paso} de 3
            </div>
          </div>
          <button
            onClick={onCerrar}
            style={{ background: 'transparent', border: 'none', fontSize: 22, cursor: 'pointer', color: COLOR_GRIS_CLARO }}
          >×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
          {paso === 1 && (
            <PasoSubir
              onArchivo={leerArchivo}
              onAbrirPlantilla={() => setModalPlantilla(true)}
              error={errorCarga}
            />
          )}
          {paso === 2 && validacion && (
            <PasoPreview
              archivo={archivo}
              validacion={validacion}
              onImportar={iniciarImportacion}
              onCancelar={() => { setPaso(1); setFilasRaw([]); setValidacion(null); setArchivo(null); }}
            />
          )}
          {paso === 3 && (
            <PasoImportacion
              estado={estado}
              onTerminar={() => { onCerrar(); onCompletado && onCompletado(); }}
            />
          )}
        </div>

        {toastPlantilla && (
          <div style={{
            position: 'absolute', bottom: 18, left: 18, right: 18,
            padding: '12px 16px', background: '#1a3a5c', color: '#fff',
            borderRadius: 10, fontSize: 12, textAlign: 'center',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          }}>
            {toastPlantilla}
          </div>
        )}
      </div>

      {modalPlantilla && (
        <ModalPlantilla
          onExcel={descargarPlantillaExcel}
          onSheets={abrirEnGoogleSheets}
          onCerrar={() => setModalPlantilla(false)}
        />
      )}
    </div>
  );
}

// ============================================
// Modal — cómo llenar la plantilla (Excel vs Google Sheets)
// ============================================
function ModalPlantilla({ onExcel, onSheets, onCerrar }) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(26,58,92,0.55)', zIndex: 1300,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
      onClick={onCerrar}
    >
      <div
        style={{
          background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 440,
          fontFamily: PP, color: COLOR_GRIS,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ fontSize: 16, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 18 }}>
          ¿Cómo quieres llenar la plantilla?
        </div>

        <div style={{
          padding: 14, border: `1px solid ${COLOR_BORDE}`, borderRadius: 10, marginBottom: 12,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 4 }}>
            📊 Excel
          </div>
          <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginBottom: 10, lineHeight: 1.5 }}>
            Descarga el CSV con soporte completo para Excel (acentos, ñ, etc.)
          </div>
          <button style={{ ...estiloBotonPrimario, width: '100%' }} onClick={onExcel}>
            📥 Descargar para Excel
          </button>
        </div>

        <div style={{
          padding: 14, border: `1px solid ${COLOR_BORDE}`, borderRadius: 10, marginBottom: 16,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 4 }}>
            📗 Google Sheets
          </div>
          <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginBottom: 10, lineHeight: 1.5 }}>
            Abre un Google Sheets nuevo y pega los datos con Ctrl+V.
          </div>
          <button
            style={{ ...estiloBotonPrimario, width: '100%', background: '#0f9d58' }}
            onClick={onSheets}
          >
            📗 Abrir Google Sheets
          </button>
        </div>

        <div style={{ textAlign: 'right' }}>
          <button style={estiloBotonSecundario} onClick={onCerrar}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// PASO 1 — subir archivo
// ============================================
function PasoSubir({ onArchivo, onAbrirPlantilla, error }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer.files && e.dataTransfer.files[0];
    if (f) onArchivo(f);
  };

  return (
    <div>
      <div style={{ fontSize: 13, color: COLOR_GRIS, marginBottom: 14, lineHeight: 1.6 }}>
        Sube un archivo CSV con sucursales. Cada fila puede traer grupo (cliente), razón social,
        contactos (encargado y gerente) y link/coordenadas de Google Maps. La
        geocodificación inversa se ejecuta automáticamente fila por fila.
      </div>

      <button
        onClick={onAbrirPlantilla}
        style={{ ...estiloBotonSecundario, marginBottom: 14 }}
      >
        📥 Usar plantilla
      </button>

      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current && inputRef.current.click()}
        style={{
          border: `2px dashed ${drag ? COLOR_PRINCIPAL : COLOR_BORDE}`,
          background: drag ? '#eaf2fb' : '#fafafa',
          borderRadius: 10, padding: 40, textAlign: 'center', cursor: 'pointer',
          transition: 'all 0.15s',
        }}
      >
        <div style={{ fontSize: 32, marginBottom: 8 }}>📄</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: COLOR_OSCURO }}>
          Arrastra tu CSV aquí o haz click para seleccionar
        </div>
        <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 6 }}>
          Solo archivos .csv (UTF-8 recomendado)
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".csv"
          onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) onArchivo(f); }}
          style={{ display: 'none' }}
        />
      </div>

      {error && (
        <div style={{ marginTop: 14, padding: 10, background: '#fde8e8', borderRadius: 8, fontSize: 12, color: '#8e3838' }}>
          ⚠️ {error}
        </div>
      )}

      <div style={{ marginTop: 18, fontSize: 11, color: COLOR_GRIS_CLARO, lineHeight: 1.6 }}>
        <strong>Columnas esperadas:</strong><br />
        {COLUMNAS.join(' · ')}
      </div>
    </div>
  );
}

// ============================================
// PASO 2 — preview y validación
// ============================================
function validar(filas) {
  const resultados = [];
  const vistas = new Map(); // clave → índice primera aparición
  let gruposUnicos = new Set();
  let razonesUnicas = new Set();
  let encargadosUnicos = new Set();
  let gerentesUnicos = new Set();

  filas.forEach((raw, i) => {
    const data = {};
    COLUMNAS.forEach(c => { data[c] = (raw[c] || '').toString().trim(); });

    const numFila = i + 2; // +2 porque la línea 1 es el header
    const advertencias = [];
    let estado = 'ok';
    let mensaje = '';

    if (!data.sucursal_nombre) {
      estado = 'error';
      mensaje = 'sucursal_nombre vacío';
    } else {
      const key = [normalizar(data.grupo), normalizar(data.razon_social), normalizar(data.sucursal_nombre)].join('|');
      if (vistas.has(key)) {
        estado = 'error';
        mensaje = `duplicada con la fila ${vistas.get(key)}`;
      } else {
        vistas.set(key, numFila);
      }
    }

    if (estado !== 'error') {
      if (!data.direccion_link && !data.direccion_manual) {
        advertencias.push('sin dirección ni link');
      }
      if (!data.razon_social && !data.grupo) {
        estado = 'error';
        mensaje = 'sin grupo ni razón social';
      }
    }

    if (estado === 'ok' && advertencias.length > 0) estado = 'advertencia';
    if (estado === 'advertencia' && !mensaje) mensaje = advertencias.join(', ');

    if (estado !== 'error') {
      if (data.grupo) gruposUnicos.add(normalizar(data.grupo));
      if (data.razon_social) razonesUnicas.add(`${normalizar(data.grupo)}|${normalizar(data.razon_social)}`);
      if (data.encargado_nombre) encargadosUnicos.add(normalizar(data.encargado_nombre) + '|' + normalizar(data.encargado_celular));
      if (data.gerente_nombre) gerentesUnicos.add(normalizar(data.gerente_nombre) + '|' + normalizar(data.gerente_celular));
    }

    resultados.push({ numFila, data, estado, mensaje, advertencias });
  });

  const conteos = {
    ok: resultados.filter(r => r.estado === 'ok').length,
    advertencia: resultados.filter(r => r.estado === 'advertencia').length,
    error: resultados.filter(r => r.estado === 'error').length,
  };

  return {
    filas: resultados,
    conteos,
    total: resultados.length,
    estimados: {
      grupos: gruposUnicos.size,
      razones: razonesUnicas.size,
      sucursales: conteos.ok + conteos.advertencia,
      encargados: encargadosUnicos.size,
      gerentes: gerentesUnicos.size,
    },
  };
}

function PasoPreview({ archivo, validacion, onImportar, onCancelar }) {
  const [filtro, setFiltro] = useState('todos');
  const [expandida, setExpandida] = useState(false);

  const filasMostradas = useMemo(() => {
    if (filtro === 'todos') return validacion.filas;
    return validacion.filas.filter(f => f.estado === filtro);
  }, [validacion, filtro]);

  const importables = validacion.conteos.ok + validacion.conteos.advertencia;
  const { estimados, conteos } = validacion;

  return (
    <div>
      <div style={{ ...estiloTarjeta, marginBottom: 14 }}>
        <div style={{ fontSize: 13, color: COLOR_GRIS, marginBottom: 12 }}>
          <strong>Archivo:</strong> {archivo.name} · <strong>Filas:</strong> {validacion.total}
        </div>
        <div style={{ display: 'grid', gap: 4, fontSize: 13 }}>
          <div style={{ color: '#1D9E75' }}>✅ Listos: <strong>{conteos.ok}</strong></div>
          <div style={{ color: '#a86b1e' }}>⚠️ Con advertencias: <strong>{conteos.advertencia}</strong></div>
          <div style={{ color: '#e05c5c' }}>❌ Con errores: <strong>{conteos.error}</strong></div>
        </div>
      </div>

      <div style={{ ...estiloTarjeta, marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 10 }}>Se crearán (estimado):</div>
        <div style={{ display: 'grid', gap: 4, fontSize: 12 }}>
          <div>· {estimados.grupos} {estimados.grupos === 1 ? 'grupo (cliente) único' : 'grupos (clientes) únicos'}</div>
          <div>· {estimados.razones} razón{estimados.razones === 1 ? '' : 'es'} social{estimados.razones === 1 ? '' : 'es'}</div>
          <div>· {estimados.sucursales} sucursal{estimados.sucursales === 1 ? '' : 'es'}</div>
          <div>· {estimados.encargados} encargado{estimados.encargados === 1 ? '' : 's'} único{estimados.encargados === 1 ? '' : 's'}</div>
          <div>· {estimados.gerentes} gerente{estimados.gerentes === 1 ? '' : 's'} único{estimados.gerentes === 1 ? '' : 's'}</div>
        </div>
        <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 10 }}>
          Los existentes se reutilizarán automáticamente (no se duplicarán).
        </div>
      </div>

      <button
        onClick={() => setExpandida(e => !e)}
        style={{ ...estiloBotonSecundario, width: '100%', marginBottom: 10 }}
      >
        {expandida ? '▲ Ocultar filas' : '▼ Ver tabla de filas'}
      </button>

      {expandida && (
        <div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
            {[
              { id: 'todos', label: `Todos (${validacion.total})` },
              { id: 'ok', label: `OK (${conteos.ok})` },
              { id: 'advertencia', label: `⚠️ (${conteos.advertencia})` },
              { id: 'error', label: `❌ (${conteos.error})` },
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setFiltro(f.id)}
                style={{
                  ...estiloBotonSecundario, padding: '6px 10px', fontSize: 11,
                  background: filtro === f.id ? COLOR_PRINCIPAL : '#fff',
                  color: filtro === f.id ? '#fff' : COLOR_GRIS, border: 'none',
                }}
              >{f.label}</button>
            ))}
          </div>
          <div style={{ maxHeight: 280, overflowY: 'auto', border: `1px solid ${COLOR_BORDE}`, borderRadius: 8 }}>
            <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
              <thead style={{ background: COLOR_FONDO, position: 'sticky', top: 0 }}>
                <tr>
                  <th style={th}>#</th>
                  <th style={th}>Sucursal</th>
                  <th style={th}>Grupo (cliente) / Razón</th>
                  <th style={th}>Encargado</th>
                  <th style={th}>Gerente</th>
                  <th style={th}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {filasMostradas.map(f => (
                  <tr key={f.numFila} style={{ borderTop: `1px solid ${COLOR_BORDE}` }}>
                    <td style={td}>{f.numFila}</td>
                    <td style={td}>{f.data.sucursal_nombre || '—'}</td>
                    <td style={td}>
                      {[f.data.grupo, f.data.razon_social].filter(Boolean).join(' / ') || '—'}
                    </td>
                    <td style={td}>{f.data.encargado_nombre || '—'}</td>
                    <td style={td}>{f.data.gerente_nombre || '—'}</td>
                    <td style={td}>
                      <span style={{
                        padding: '2px 6px', borderRadius: 4, fontSize: 10,
                        background: f.estado === 'ok' ? '#e8faf5' : f.estado === 'advertencia' ? '#fff8ed' : '#fde8e8',
                        color: f.estado === 'ok' ? '#1D9E75' : f.estado === 'advertencia' ? '#a86b1e' : '#e05c5c',
                      }}>
                        {f.estado === 'ok' ? 'OK' : f.estado === 'advertencia' ? '⚠️' : '❌'}
                      </span>
                      {f.mensaje && <div style={{ fontSize: 10, color: COLOR_GRIS_CLARO, marginTop: 2 }}>{f.mensaje}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
        <button style={estiloBotonSecundario} onClick={onCancelar}>Cancelar</button>
        <button
          style={{ ...estiloBotonPrimario, opacity: importables === 0 ? 0.5 : 1 }}
          onClick={onImportar}
          disabled={importables === 0}
        >
          Importar {importables} filas
        </button>
      </div>
    </div>
  );
}

const th = { padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: COLOR_GRIS, fontSize: 10 };
const td = { padding: '6px 8px', verticalAlign: 'top', color: COLOR_GRIS };

// ============================================
// PASO 3 — importación con progreso
// ============================================
function PasoImportacion({ estado, onTerminar }) {
  if (!estado) {
    return <div style={{ textAlign: 'center', padding: 40, color: COLOR_GRIS_CLARO }}>Iniciando…</div>;
  }
  if (estado.estado === 'error') {
    return (
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#e05c5c', marginBottom: 10 }}>❌ Error</div>
        <div style={{ fontSize: 12, color: COLOR_GRIS }}>{estado.errorFatal || 'Error desconocido'}</div>
        <div style={{ marginTop: 20, textAlign: 'right' }}>
          <button style={estiloBotonSecundario} onClick={onTerminar}>Cerrar</button>
        </div>
      </div>
    );
  }

  const pct = estado.total > 0 ? Math.round((estado.procesadas / estado.total) * 100) : 0;
  const terminado = estado.estado === 'completado';

  return (
    <div>
      {!terminado && (
        <div>
          <div style={{ fontSize: 13, color: COLOR_OSCURO, marginBottom: 10 }}>
            ⏳ Procesando fila {estado.procesadas} de {estado.total}…
          </div>
          <div style={{
            height: 10, background: COLOR_FONDO, borderRadius: 5, overflow: 'hidden',
            border: `1px solid ${COLOR_BORDE}`,
          }}>
            <div style={{
              width: `${pct}%`, height: '100%', background: COLOR_PRINCIPAL,
              transition: 'width 0.3s',
            }} />
          </div>
          <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 6 }}>{pct}%</div>
          <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 14 }}>
            Nominatim se consulta secuencialmente (máx 1 req/seg) — esto puede tardar varios minutos en batches grandes.
          </div>
        </div>
      )}

      {terminado && (
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1D9E75', marginBottom: 14 }}>
            ✅ Importación completada
          </div>
          <div style={{ ...estiloTarjeta, marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 8 }}>Creados</div>
            <div style={{ display: 'grid', gap: 4, fontSize: 12 }}>
              <div>· {estado.creados.grupos} {estado.creados.grupos === 1 ? 'grupo (cliente) nuevo' : 'grupos (clientes) nuevos'}</div>
              <div>· {estado.creados.razones} razón{estado.creados.razones === 1 ? '' : 'es'} social{estado.creados.razones === 1 ? '' : 'es'}</div>
              <div>· {estado.creados.sucursales} sucursal{estado.creados.sucursales === 1 ? '' : 'es'}</div>
              <div>· {estado.creados.personas} persona{estado.creados.personas === 1 ? '' : 's'}</div>
              <div>· {estado.creados.roles} rol{estado.creados.roles === 1 ? '' : 'es'} asignado{estado.creados.roles === 1 ? '' : 's'}</div>
            </div>
          </div>
          {estado.advertencias && estado.advertencias.length > 0 && (
            <details style={{ ...estiloTarjeta, marginBottom: 10 }}>
              <summary style={{ fontSize: 13, fontWeight: 600, color: '#a86b1e', cursor: 'pointer' }}>
                ⚠️ Advertencias ({estado.advertencias.length})
              </summary>
              <div style={{ marginTop: 10, maxHeight: 160, overflowY: 'auto', fontSize: 11 }}>
                {estado.advertencias.map((a, i) => (
                  <div key={i} style={{ padding: '3px 0', color: COLOR_GRIS }}>
                    Fila {a.fila}: {a.mensaje}
                  </div>
                ))}
              </div>
            </details>
          )}
          {estado.errores && estado.errores.length > 0 && (
            <details style={{ ...estiloTarjeta, marginBottom: 10, borderColor: '#f0c9c9' }}>
              <summary style={{ fontSize: 13, fontWeight: 600, color: '#e05c5c', cursor: 'pointer' }}>
                ❌ Errores ({estado.errores.length})
              </summary>
              <div style={{ marginTop: 10, maxHeight: 160, overflowY: 'auto', fontSize: 11 }}>
                {estado.errores.map((e, i) => (
                  <div key={i} style={{ padding: '3px 0', color: COLOR_GRIS }}>
                    Fila {e.fila}: {e.error}
                  </div>
                ))}
              </div>
            </details>
          )}
          <div style={{ marginTop: 20, textAlign: 'right' }}>
            <button style={estiloBotonPrimario} onClick={onTerminar}>
              Ver en Clientes
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
